import {shallowEqual, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'

/**
 * Who may use the firmware screens. TWO permissions, deliberately.
 *
 * - **Rollouts** (start, history, retry/expire): Admin and Company. Kalle's ITOP-925 decisions
 *   1b/2c, 2026-10-05, released by Cristian as ITOP-928. This replaces Cristian's 2026-08-21
 *   ruling "Admin only. Not Distributor, and explicitly not Factory" for ROLLOUTS only;
 *   Distributor and Factory are still refused.
 * - **Builds** (the global registry, and the configured-version control on that page): Admin
 *   only (7a). A Company never registers, withdraws or restores a build, and never sets which
 *   firmware a group should run.
 *
 * One hook used to gate both, so it could not be widened for Company without handing Company
 * the registry. Keep them separate.
 *
 * `PG` and `TA` are the portal's names for the backend's Admin and Company:
 * `verifyRolesAndSet` (AuthRedux.ts:42) collapses the backend's UserType array into one scalar
 * by fixed priority, renaming on the way through.
 *
 * **These gates are cosmetic. The backend is the enforcement** (ITOP-927: every rollout
 * endpoint checks that the caller owns the devices and the rollout). If the backend stops
 * checking, a Company login can reach other companies' vehicles by calling the API directly,
 * whatever this file says.
 */
const ROLLOUT_ROLES = ['PG', 'TA']
const BUILDS_ROLES = ['PG']

export const canRunFirmwareRollout = (role: string | null | undefined): boolean =>
  typeof role === 'string' && ROLLOUT_ROLES.includes(role)

export const canManageFirmwareBuilds = (role: string | null | undefined): boolean =>
  typeof role === 'string' && BUILDS_ROLES.includes(role)

const useRole = () =>
  useSelector<RootState, string | null | undefined>(({auth}) => auth.role, shallowEqual)

export function useCanRunFirmwareRollout(): boolean {
  return canRunFirmwareRollout(useRole())
}

export function useCanManageFirmwareBuilds(): boolean {
  return canManageFirmwareBuilds(useRole())
}
